import { Breadcrumbs, IconButton, Theme, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import KenColors from '../../Helpers/Themes/kenColors';
import routes from '../../Utils/routes';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const useStyles = makeStyles((theme: Theme) => ({
  mainText: {
    fontWeight: '700 !important',
    letterSpacing: '-0.3px',
    textDecoration: 'none',
    textTransform: 'capitalize',
    margin: '0px !important',
    padding: '0px !important',
    '@media (min-width: 1280px) and (max-width: 1300px)': {
      fontSize: '13px !important',
    },
  },
  listText: {
    textDecoration: 'none',
    textTransform: 'capitalize',
    margin: '0px !important',
    padding: '0px !important',
  },
  crumbs: {
    marginTop: '4px !important',
  },
  crumbItem: {
    fontSize: '0.875rem !important',
    fontWeight: '400 !important',
    color: `${KenColors.CttMutedForeground} !important`,
    textDecoration: 'none',
    textTransform: 'capitalize',
  },
}));

// Friendly titles for routes whose raw path segment doesn't read well as a title (e.g. "student-portal").
const ROUTE_TITLES: Record<string, string> = {
  '/home': 'Home',
  '/academics/activities and resources': 'My Batch',
  '/assessment': 'Activities',
  '/assessment/new form': 'New Form',
  '/student-portal': 'Home',
  '/student-portal/courses': 'My Batch',
  '/student-portal/course-content': 'Course Content',
  '/student-portal/activities': 'Activities',
  '/student-portal/tests': 'Pre & Post test',
  '/student-portal/tests/attempt': 'Pre & Post test',
  '/student-portal/artifacts': 'Artifacts',
  '/student-portal/certificates': 'Certificates',
  '/student-portal/attendance': 'My Attendance',
  '/student-portal/timetable': 'Timetable',
  '/student-portal/mentoring': 'Mentoring',
  '/student-portal/forum': 'Discussion Forum',
  '/student-portal/profile': 'My Profile',
  '/forumdiscussion': 'Discussion Forum',
};

const AppBreadCrumbs: React.FunctionComponent = () => {
  const { pathname, state } = useLocation();
  console.log("Current Path:", pathname);
  const classes = useStyles();
  const navigate = useNavigate();

  // Student portal pages are flat (no useful breadcrumb trail) - just show the friendly title.
  const normalizedPath = pathname.toLowerCase().replace(/\/+$/, '') || '/';
  const rawPath = decodeURIComponent(pathname);
  if (normalizedPath.startsWith('/student-portal')) {
    return (
      <Typography className={classes.mainText} color={KenColors.ClientBlack2} fontSize={'20px'}>
        {ROUTE_TITLES[normalizedPath] ?? 'Home'}
      </Typography>
    );
  }

  // Per-course detail page (/academics/course/:courseId) - "Academics" as a
  // lead-in isn't useful once you're inside a specific course, so show just
  // "Course" with no breadcrumb trail above it.
  if (/^\/academics\/course\/[^/]+$/.test(normalizedPath)) {
    return (
      <Typography className={classes.mainText} color={KenColors.ClientBlack2} fontSize={'20px'}>
        Course
      </Typography>
    );
  }

  // The faculty-courses segment is an implementation route for attendance
  // and should not appear as a navigable breadcrumb in the UI.
  const attendanceSessionMatch = rawPath.match(/^\/attendance\/Faculty Courses\/Session Attendance\/[^/]+$/i);
  if (attendanceSessionMatch) {
    return (
      <>
        <Typography className={classes.mainText} color={KenColors.ClientBlack2} fontSize={'20px'}>
          Mark Attendance
        </Typography>
        <Breadcrumbs
          className={classes.crumbs}
          separator={<Typography className={classes.crumbItem} sx={{ fontSize: '0.875rem !important' }}>›</Typography>}
        >
          <Link className={classes.listText} to={routes.attendance}>
            <Typography className={classes.crumbItem}>Attendance</Typography>
          </Link>
          <Typography className={classes.crumbItem}>Session</Typography>
        </Breadcrumbs>
      </>
    );
  }

  // Process the current path
  let navigateHistory = rawPath.split('/').filter(item => item && item !== '');

  // Clean up breadcrumb items - remove ID parts
  const cleanBreadcrumbs = [...navigateHistory];
  const originalFullPath = [...navigateHistory];

  // Simple ID pattern detection (assumes IDs follow specific named segments)
  for (let i = 0; i < cleanBreadcrumbs.length; i++) {
    const current = cleanBreadcrumbs[i];
    const prev = i > 0 ? cleanBreadcrumbs[i - 1] : '';

    // Check if the current item looks like an ID (after specific segments)
    const isIDAfterSpecificSegment =
      (prev === 'Student Details' ||
        prev === 'Student Info' ||
        prev === 'Session Attendance') &&
      (/^[a-zA-Z0-9-]+$/.test(current));

    if (isIDAfterSpecificSegment) {
      // Remove the ID from display but keep track of the original path
      cleanBreadcrumbs.splice(i, 1);
      i--; // Adjust index since we removed an element
    }
  }

  const isResourcesPath = pathname.endsWith('/Resources');

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {isResourcesPath && (
          <IconButton onClick={() => navigate(-1)} size="small">
            <ArrowBackIcon />
          </IconButton>
        )}
        <Typography className={classes.mainText} color={KenColors.ClientBlack2} fontSize={'20px'}>
          {ROUTE_TITLES[normalizedPath] ?? cleanBreadcrumbs[0]}
        </Typography>
      </div>
      {cleanBreadcrumbs.length > 1 && (
        <Breadcrumbs
          className={classes.crumbs}
          separator={<Typography className={classes.crumbItem} sx={{ fontSize: '0.875rem !important' }}>›</Typography>}
        >
          {cleanBreadcrumbs.map((item, index) => {
            // Build path up to this segment (preserving IDs in the actual path)
            let currentPathSegments = [];
            let segmentCounter = 0;

            // This simpler approach rebuilds the path including IDs
            for (let i = 0; i < originalFullPath.length; i++) {
              currentPathSegments.push(originalFullPath[i]);

              // If this segment matches our current breadcrumb and we've found enough matches
              if (originalFullPath[i] === item && segmentCounter === index) {
                break;
              }

              // Only count non-ID segments for matching against our clean breadcrumb
              const prev = i > 0 ? originalFullPath[i - 1] : '';
              const isIDSegment =
                (prev === 'Student Details' ||
                  prev === 'Student Info' ||
                  prev === 'Session Attendance') &&
                (/^[a-zA-Z0-9-]+$/.test(originalFullPath[i]));

              if (!isIDSegment) {
                segmentCounter++;
              }
            }

            // Build the path including any IDs
            const newPath = '/' + currentPathSegments.join('/');
            let displayItem = ROUTE_TITLES[newPath.toLowerCase()] ?? item.replace(/-/g, ' ');

            return index === cleanBreadcrumbs.length - 1 && state?.breadCrumbs ? (
              <Typography key={index} className={classes.crumbItem}>
                {state.breadCrumbs}
              </Typography>
            ) : (
              <Link key={index} className={classes.listText} to={newPath}>
                <Typography className={classes.crumbItem}>
                  {displayItem}
                </Typography>
              </Link>
            );
          })}
        </Breadcrumbs>
      )}
    </>
  );
};

export default AppBreadCrumbs;
